import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sprout, ArrowRight, X, Circle, CheckCircle2 } from "lucide-react";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { listPwfs, type Pwf } from "../../../api/pwf";
import { toggleStatus, type UserTakeaway } from "../../../api/userTakeaways";
import { useSpaces } from "../../../context/SpaceProvider";
import { useNextStepsByProject } from "../../../hooks/useNextStepsByProject";
import { usePatchCockpitConfig } from "./cockpitState";
import { lifeProjectOf } from "./LifeProjects";
import { trackHomeEvent } from "../../../lib/homeAnalytics";
import { queryClient } from "../../../lib/queryClient";
import { scrollReveal } from "../motionConstants";

export const PlantedCard = ({ onOpenProjects }: { onOpenProjects: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { spaces } = useSpaces();
  const patchCockpit = usePatchCockpitConfig();
  const { nextStepByProject } = useNextStepsByProject();
  const viewed = useRef(false);
  const [busy, setBusy] = useState(false);

  const { data: pwfs } = useQuery({
    queryKey: ["cockpit-projects"],
    queryFn: () => listPwfs(retryFetch),
  });
  const activeProjects = (pwfs ?? [])
    .filter(
      (p: Pwf) =>
        !p.isTemplate &&
        !p.pwfMeta?.fiParent &&
        lifeProjectOf(p)?.status === "promote" &&
        !["archived", "completed"].includes(p.lifecycleState)
    )
    .slice(0, 4);

  useEffect(() => {
    if (!viewed.current && activeProjects.length > 0) {
      viewed.current = true;
      trackHomeEvent("arrangement_viewed", { projects: activeProjects.length });
    }
  }, [activeProjects.length]);

  const dismiss = () => {
    trackHomeEvent("arrangement_dismissed", {});
    patchCockpit({ plantedCollapsedAt: new Date().toISOString() }).catch(() => {});
  };

  const completeStep = async (tk: UserTakeaway) => {
    if (busy) return;
    setBusy(true);
    try {
      await toggleStatus(retryFetch, { id: tk._id });
      queryClient.invalidateQueries({ queryKey: ["home-today-actions"] });
    } catch {
      toast.error(t("action_save_error", "Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  const openProject = (projectId: string) => {
    trackHomeEvent("arrangement_project_clicked", { projectId });
    navigate(`/project/${projectId}`);
  };

  const spaceColor = (name?: string) =>
    (spaces ?? []).find((s: any) => s.name === name)?.color || "#6366f1";
  const identityFor = (name?: string) =>
    (spaces ?? []).find((s: any) => s.name === name)?.identityLabel ?? null;

  if (activeProjects.length === 0) return null;

  return (
    <motion.section
      {...scrollReveal}
      className="relative rounded-2xl border border-primary/15 bg-gradient-to-br from-card via-card to-primary/[0.03] p-5 md:p-6 overflow-hidden"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sprout className="w-[18px] h-[18px] text-primary" />
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/80">
          {t("planted_title")}
        </p>
        <button
          onClick={onOpenProjects}
          className="ms-auto inline-flex items-center gap-1 text-xs text-primary/80 hover:text-primary min-h-[44px] px-2"
        >
          {t("planted_open_map")}
          <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </button>
        <button
          onClick={dismiss}
          aria-label={t("dismiss", "Dismiss")}
          className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] -my-2 -me-2 text-muted-foreground/50 hover:text-muted-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {(spaces ?? []).map((s: any) => (
          <span
            key={s._id}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-2.5 py-1 text-[11.5px] text-foreground/80"
          >
            <span className="w-2 h-2 rounded-full flex-none" style={{ background: s.color || "#6366f1" }} />
            {s.name}
          </span>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-2.5">
        {activeProjects.map((p: Pwf) => {
          const lp = lifeProjectOf(p);
          const next = nextStepByProject[p._id];
          return (
            <div
              key={p._id}
              role="presentation"
              onClick={() => openProject(p._id)}
              className="rounded-xl border border-border/60 bg-card px-4 py-3 hover:shadow-md hover:border-primary/25 transition-all cursor-pointer min-h-[44px]"
            >
              {lp?.spaceName && (
                <p className="text-[10.5px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: spaceColor(lp.spaceName) }}>
                  {lp.spaceName}
                </p>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openProject(p._id); }}
                className="text-start block w-full"
              >
                <span className="text-sm font-semibold text-foreground leading-snug line-clamp-2">{p.title}</span>
              </button>
              {identityFor(lp?.spaceName ?? undefined) && (
                <p className="text-[11.5px] text-muted-foreground mt-0.5 line-clamp-1">
                  {identityFor(lp?.spaceName ?? undefined)}
                </p>
              )}
              {next && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (!busy) completeStep(next); }}
                  aria-disabled={busy}
                  className={`group mt-2 flex items-center gap-1.5 text-[12px] text-foreground/75 min-h-[44px] w-full text-start${busy ? " opacity-50" : ""}`}
                >
                  <Circle className="w-3.5 h-3.5 flex-none text-muted-foreground/40 group-hover:hidden" />
                  <CheckCircle2 className="w-3.5 h-3.5 flex-none text-primary hidden group-hover:block" />
                  <span className="line-clamp-2">{next.content}</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11.5px] text-muted-foreground/70 leading-relaxed mt-3.5">
        {t("planted_footnote")}
      </p>
    </motion.section>
  );
};
